var _class, _descriptor, _descriptor2;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Adapter from '@ember-data/adapter';
import { isArray, A } from '@ember/array';
import { isBlank, isEmpty, isNone } from '@ember/utils';
import { inject as service } from '@ember/service';
import { CatalogConnectV2Client, CatalogObjectBatch } from '@square/dashboard-items-data/clients/catalog-connect-v2';
import { CatalogObjectType } from '@square/dashboard-items-data/models/connect/catalog-object';
import UUID from 'uuid-js';
import { recordIdentifierFor } from '@ember-data/store';
import { assert } from '@ember/debug';
function commitSaveToRecord(store, modelName, record, responseObject) {
  const modelClass = store.modelFor(modelName);
  const normalized = store.serializerFor(modelName).normalizeForPush(modelClass, responseObject);
  const identifier = recordIdentifierFor(record);
  normalized.data.lid = identifier.lid;
  store.push(normalized);

  // pushing the response data doesn't actually "clean" the record, any locally changed attributes
  // that are different from the server response will still be considered dirty. However, in this case we just saved
  // the record's data so if any of the attributes are different it's because the server overwrote them.
  // We rollback to get the model back to a clean state with only the server data.
  record.rollbackAttributes();
}

// Mirrors countObjects() in go-square/items/connectv2/merchant_catalog.go.
// The Catalog API counts nested CatalogObjects (variations, modifiers, etc.)
// toward the per-batch object limit.
function _countObject(obj) {
  let count = 1;
  switch (obj.type) {
    case 'ITEM':
      count += obj.item_data?.variations?.length ?? 0;
      break;
    case 'ITEM_OPTION':
      count += obj.item_option_data?.values?.length ?? 0;
      break;
    case 'MODIFIER_LIST':
      count += obj.modifier_list_data?.modifiers?.length ?? 0;
      break;
    case 'SUBSCRIPTION_PLAN':
      {
        const planVariations = obj.subscription_plan_data?.subscription_plan_variations ?? [];
        count += planVariations.length;
        for (const pv of planVariations) {
          count += pv.subscription_plan_variation_data?.phases?.length ?? 0;
        }
        break;
      }
    case 'SUBSCRIPTION_PLAN_VARIATION':
      count += obj.subscription_plan_variation_data?.phases?.length ?? 0;
      break;
    case 'CHECKOUT_LINK':
      count += obj.checkout_link_data?.order_details?.line_items?.length ?? 0;
      break;
    default:
      break;
  }
  return count;
}
const FIND_ALL_DISALLOWED_TYPES = new Set([CatalogObjectType.ITEM, CatalogObjectType.ITEM_VARIATION]);
let CatalogObject = (_class = class CatalogObject extends Adapter {
  constructor(args) {
    super(args);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "features", _descriptor2, this);
    this.useFetch = false;
    this.hasFetchedAll = false;
  }
  shouldBackgroundReloadAll() {
    return false;
  }

  // This hasFetchedAll pattern originates from Dashboard code.
  // There are some cases where we push data into the Ember Data store
  // before we've actually called findAll() for a given model. By default,
  // if Ember Data sees that there are already records pushed into the
  // store, it'll immediately return a RecordArray of those items and
  // fire a reload in the background. What we actually want is for Ember Data
  // to do a full (re)load the first time findAll is called.
  shouldReloadAll() {
    return !this.hasFetchedAll;
  }
  async findRecord(store, type, id, options = {}) {
    const payload = await CatalogConnectV2Client.retrieveCatalogObject({
      ...options.adapterOptions,
      objectId: id,
      includeRelatedObjects: options?.adapterOptions?.includeRelatedObjects ?? false
    }, {
      headers: this.headers
    });
    if (payload.object && payload.related_objects) {
      payload.object.related_objects = payload.related_objects;
    }
    return payload.object;
  }
  findMany(store, type, ids, snapshots) {
    const adapterOptions = A(snapshots).firstObject.adapterOptions;
    return CatalogConnectV2Client.batchRetrieveCatalogObjects({
      objectIds: ids,
      ...adapterOptions
    }, {
      headers: this.headers
    }).then(({
      objects = [],
      related_objects
    }) => {
      // Push all related objects into store
      if (objects.length > 0 && related_objects) {
        objects[0].related_objects = related_objects;
      }
      return objects;
    });
  }

  /*
  The goal of this check is to prevent new UIs from being built in a non-scalable way because an engineer/team
  is unaware of the performance implications of trying to prefetch large portions of a merchant's catalog.
  Prefetching seems like an attractive approach for many teams because it simplifies the application code after
  loading in the route, but we should be focused on creating scalable features that work well for all merchants,
  including those with large catalogs.
  */
  _verifyFindAllIsAllowed(objectType) {
    if (FIND_ALL_DISALLOWED_TYPES.has(objectType)) {
      assert(`fetching all models of type ${objectType} is strongly discouraged. The number of objects of
          this type in a merchant's catalog is unbounded and often extremely large in practice, meaning attempts to
          fetch all objects for a merchant can take multiple minutes or fail completely for merchants with large catalogs.
          All new features utilizing this model should be implemented in a scalable way by fetching data in pages or
          with limited queries. Visit #oc-catalog-dashboard-support for assistance.`);
    }
  }

  // pages through and collects all objects of a given type
  async _listAllObjects(store, type) {
    const objectType = type.attributes.get('type').options.defaultValue;
    this._verifyFindAllIsAllowed(objectType);
    const params = {
      types: [objectType]
    };
    const options = {
      headers: this.headers
    };
    const allObjects = [];
    do {
      const response = await CatalogConnectV2Client.listCatalog(params, options);
      const objects = response.objects || [];
      allObjects.push(...objects);
      params.cursor = response.cursor;
    } while (!isBlank(params.cursor));
    return allObjects;
  }
  async findAll(store, type) {
    const objects = await this._listAllObjects(store, type);
    this.hasFetchedAll = true;
    return objects;
  }
  query(store, type, query, {
    adapterOptions = {}
  } = {}) {
    const objectType = type.attributes.get('type').options.defaultValue;
    const params = {
      objectTypes: [objectType],
      ...adapterOptions,
      ...query,
      // TODO: Find a way to make this look better. Adapter options are sometimes inside query
      ...query?.adapterOptions
    };
    return CatalogConnectV2Client.searchCatalogObjects(params, {
      headers: params.headers ?? this.headers
    }).then(({
      objects = [],
      cursor,
      related_objects
    }) => {
      // Push all related objects into store
      if (objects.length > 0 && related_objects) {
        objects[0].related_objects = related_objects;
      }
      objects.meta = {
        cursor
      };
      return objects;
    });
  }
  async createRecord(store, type, snapshot) {
    const {
      responseObjects
    } = await this._upsertRecords([snapshot.record], snapshot.adapterOptions);
    return responseObjects?.[0];
  }
  async updateRecord(store, type, snapshot) {
    const {
      responseObjects
    } = await this._upsertRecords([snapshot.record]);
    return responseObjects?.[0];
  }

  // Persists data to server. Also replaces temp ids on passed models with server ids.
  async _upsertRecords(records, options = {}) {
    options.headers = {
      ...this.headers,
      ...options.headers
    };
    const serializedObjects = records.map(record => record.serialize());
    let batches;
    if (this.features.isEnabled('catalog-enable-batch-upsert-object-count-chunking')) {
      // Split into multiple batches based on total object count (including
      // nested CatalogObjects like variations). Mirrors countObjects() in
      // go-square/items/connectv2/merchant_catalog.go. Max 1000 per batch.
      const MAX_BATCH_OBJECTS = 1000;
      batches = [];
      let currentBatch = [];
      let currentCount = 0;
      for (const obj of serializedObjects) {
        const objCount = _countObject(obj);
        if (currentCount + objCount > MAX_BATCH_OBJECTS && currentBatch.length > 0) {
          batches.push(CatalogObjectBatch.create({
            objects: currentBatch
          }));
          currentBatch = [];
          currentCount = 0;
        }
        currentBatch.push(obj);
        currentCount += objCount;
      }
      if (currentBatch.length > 0) {
        batches.push(CatalogObjectBatch.create({
          objects: currentBatch
        }));
      }
    } else {
      batches = [CatalogObjectBatch.create({
        objects: serializedObjects
      })];
    }
    const {
      objects,
      id_mappings = []
    } = await CatalogConnectV2Client.batchUpsertCatalogObjects({
      batches,
      idempotencyKey: UUID.create().toString(),
      ignoreItemDefaults: options.ignoreItemDefaults
    }, options);
    this._replaceTempIds(records, id_mappings);
    return {
      responseObjects: objects,
      idMappings: id_mappings
    };
  }
  _replaceTempIds(records, idMappings) {
    const idMap = idMappings.reduce((map, mapping) => {
      map[mapping.client_object_id] = mapping.object_id;
      return map;
    }, {});
    records.forEach(record => {
      const serializer = this.store.serializerFor(this._modelNameForObjectType(record.type));
      for (const [key, config] of Object.entries(serializer.attrs || {})) {
        if (config?.embedded === 'always' && !isEmpty(record[key])) {
          const embeddedRecords = isArray(record[key]) ? record[key] : [record[key]];
          if (isNone(embeddedRecords.firstObject.idOrTempId)) {
            return;
          }
          embeddedRecords.forEach(embeddedRecord => {
            if (!embeddedRecord.id && idMap[embeddedRecord.idOrTempId()]) {
              embeddedRecord.set('id', idMap[embeddedRecord.idOrTempId()]);
            }
          });
          this._replaceTempIds(embeddedRecords, idMappings);
        }
      }
    });
  }
  deleteRecord(store, type, snapshot) {
    return CatalogConnectV2Client.deleteCatalogObject({
      objectId: snapshot.id
    }, {
      headers: this.headers
    });
  }

  /**
   * Method to batch deleted multiple objects in a single API call
   * NOTE: Method will delete what it can and does not roll back deletes on failures
   *
   * @param {Array<CatalogObject>} objects
   * @returns Resolve with hash of response, which includes `deleted_object_ids` and `unmatchedDeletedObjectIds` a list of object_ids that are deleted but we could not match and remove from ember data here
   * Some deletes might include child data which gets returned as a deleted ID.
   * This function doesn't have reference to the models, so we return these unmatched deleted ids and allow callers to further identify them if needed
   */
  async batchDeleteCatalogObjects(objects) {
    const objectIds = objects.map(object => object.id);
    const response = await CatalogConnectV2Client.batchDeleteCatalogObjects({
      objectIds
    }, {
      headers: this.headers
    });
    const unmatchedDeletedObjectIds = [];
    response.deleted_object_ids.forEach(deletedObjectId => {
      const object = objects.find(object => object.id === deletedObjectId);
      if (!object) {
        unmatchedDeletedObjectIds.push(deletedObjectId);
        return;
      }
      // mark as isDeleted for any local reference data
      object.deleteRecord();
      // remove from the store
      object.unloadRecord();
    });
    return {
      ...response,
      unmatchedDeletedObjectIds
    };
  }

  /**
   * EmberData doesn't have any first class support for batch saving records. This adapter method can be used to batch save
   * multiple CatalogObjects and have their data and states correctly updated in the EmberData store.
   *
   * Example usage:
   *   `this.store.adapterFor('custom-attribute-definition-ed').batchRecords([model1, model2, model3]);`
   *   `this.store.adapterFor('custom-attribute-definition-ed').batchRecords([model1, model2, model3], { headers: { Version: 1 } });`
   *
   * @param {Array<CatalogObject>} records - CatalogObject models to save
   * @param {Object=} options - sent in ajax request
   * @returns {Promise<Object>} resolves with a hash of `savedObjects` and `idMappings`
   */
  async batchRecords(records, options) {
    const {
      responseObjects,
      idMappings
    } = await this._upsertRecords(records, options);
    responseObjects.forEach((object, i) => {
      commitSaveToRecord(this.store, this._modelNameForObjectType(object.type), records.objectAt(i), object);
    });
    return {
      savedObjects: records,
      idMappings
    };
  }
  _modelNameForObjectType(type) {
    return this.store.serializerFor('connect/catalog-object')._modelNameForObjectType(type);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "features", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
export { CatalogObject as default };